import { StoreWithProps, useClassState } from "@web-monorepo/hooks";
import type { ToolListItem, ToolPayload } from "src/pods/toolkitData/components/EditableSetup";

export type UseDirectionsEditorProps = {
  onEdit: (updatedPayload: ToolPayload) => void;
  payload: ToolPayload;
  setFocusOnParent?: (focus: boolean) => void;
};

export type UseDirectionsEditorResult = {
  list: ToolListItem[];
  title: string;
  askForNewItem: boolean;
  newItemText: string | null;
  canSubmit: boolean;
  setNewItemText: (text: string) => void;
  openNewItemInput: () => void;
  cancelNewItem: () => void;
  submitNewItem: () => void;
  onTitleChange: (next: string) => void;
  deleteItem: (index: number) => void;
};

/**
 * Valtio-backed store for DirectionsEditor's "add a new step" modal. Holds:
 *   - the transient `askForNewItem` toggle for whether the modal is open
 *   - the in-progress `newItemText` while the user types
 *   - derived `canSubmit` (truthy text)
 *   - the title + list derivations from props.payload for consumer convenience
 * The submitted list mutates the parent toolkit data store via props.onEdit.
 */
class DirectionsEditorStore extends StoreWithProps<UseDirectionsEditorProps> {
  askForNewItem = false;
  newItemText: string | null = null;

  get list(): ToolListItem[] {
    return this.props.payload?.list ?? [];
  }

  get title(): string {
    return this.props.payload?.title ?? "";
  }

  get canSubmit(): boolean {
    return Boolean(this.newItemText);
  }

  setNewItemText(text: string) {
    this.newItemText = text;
  }

  openNewItemInput() {
    this.props.setFocusOnParent?.(false);
    this.askForNewItem = true;
    this.newItemText = null;
  }

  closeNewItemInput() {
    this.askForNewItem = false;
    this.newItemText = null;
  }

  cancelNewItem() {
    this.props.setFocusOnParent?.(true);
    this.closeNewItemInput();
  }

  submitNewItem() {
    const text = this.newItemText;
    if (!text) return;
    this.props.setFocusOnParent?.(true);
    this.emit({ list: [...this.list, { text }] });
    this.closeNewItemInput();
  }

  onTitleChange(next: string) {
    this.emit({ title: next });
  }

  deleteItem(index: number) {
    this.emit({ list: this.list.filter((_, i) => i !== index) });
  }

  private emit(update: Partial<ToolPayload>) {
    const merged: ToolPayload = { list: [], title: "", ...this.props.payload, ...update };
    this.props.onEdit(merged);
  }
}

export function useDirectionsEditor(props: UseDirectionsEditorProps): UseDirectionsEditorResult {
  const store = useClassState(DirectionsEditorStore, props);

  return {
    list: store.list,
    title: store.title,
    askForNewItem: store.askForNewItem,
    newItemText: store.newItemText,
    canSubmit: store.canSubmit,
    setNewItemText: store.setNewItemText,
    openNewItemInput: store.openNewItemInput,
    cancelNewItem: store.cancelNewItem,
    submitNewItem: store.submitNewItem,
    onTitleChange: store.onTitleChange,
    deleteItem: store.deleteItem,
  };
}
